//#region Imports Packages and Libs

import { __vueEvent } from "../../../constant";
import { EVENTS } from "../../../enums";
import { Util } from "../../../util";
import { ActionResolver } from "../utils/action_resolver";
import { InlineClickAction, MessageStructureComponent } from "./../../../types";
import { PostbackHandler } from "./postback_handler";

//#endregion

//#region InlineActionDispatcher Class

/**
 * Handles the dispatching of inline actions triggered by user interactions within a message component.
 *
 * The `InlineActionDispatcher` class provides static methods to validate and dispatch inline click actions,
 * such as postbacks, based on user input and URL parameters. It ensures that only valid actions with
 * appropriate postback values are processed.
 */
export class InlineActionDispatcher {
	/**
	 * Determines whether the given inline click action contains a valid postback value.
	 *
	 * @param action - The inline click action to validate.
	 * @returns `true` if the action has a non-empty postback value; otherwise, `false`.
	 */
	static isPostbackValid(action: InlineClickAction): boolean {
		return !!action.postback && !Util.isNullOrEmpty(action.postback);
	}

	/**
	 * This method extracts an action key from the given URL, retrieves the corresponding
	 * action from the `clickActions` map, and executes the postback action if it is valid.
	 * If the action is not found or is invalid, the method safely returns without performing any action.
	 *
	 * @param url - The URL containing the action key as a query parameter.
	 * @param component - The message structure component associated with the action.
	 * @param clickActions - A map of action keys to their corresponding inline click actions.
	 */
	static dispatch(
		url: string,
		component: MessageStructureComponent,
		clickActions: Record<string, InlineClickAction>
	): void {
		try {
			// Validate the input parameters
			if (!clickActions || !url || typeof url !== "string") return;

			// 1: Get actionKey from the URL query parameter
			const actionKey = ActionResolver.getActionKeyFromUrl(url);
			if (!actionKey || !clickActions[actionKey]) return;

			// 2: Get the action object from the clickActions map
			const action = clickActions[actionKey];
			if (!action) return;

			// 3: Emit pre action event
			__vueEvent.emit(EVENTS.ButtonClick);

			// STEP 4: If no postback action is defined, then skip doing anything
			// Else, trigger the associated postback action as if a button was clicked
			if (this.isPostbackValid(action)) {
				PostbackHandler.execute(action, component);
			} else {
				console.log("[x] No 'postback' defined for this inline click action.");
			}
		} catch (ex) {
			console.error("[inline_action_dispatcher.ts] Error in dispatching action:", ex);
		}
	}
}

//#endregion
